import { ProductMedia } from './product-media'
import { Variant } from 'src/scripts/types/shopify'
import { ProductForm, ProductMediaCarousel, QuickView } from '.'
import { BaseComponent } from '../../components/base-component'
import { FloatElement } from 'src/scripts/components/float-element'
import { $el, $list, $elParent, whenDefined } from 'src/scripts/utils/utils'
import { isEnterKey, isTabKey } from 'src/scripts/utils/key'
import { HeaderComponent } from '../header'

const PRODUCT_BLOCK_SELECTOR = 'data-product-block'
const PRODUCT_MEDIA_SELECTOR = 'data-product-media-container'
const OPTION_INPUTS_SELECTOR = '[data-variant-picker-option-inputs]'
const OPTION_LABELS_SELECTOR = '[data-variant-picker-option-labels]'
const SELECTED_VARIANT_IMAGE_SELECTOR = 'data-selected-variant-image-id'
const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
const HEADER_SELECTOR = 'header-component'

export class VariantPicker extends BaseComponent {
  variant: Variant
  quickView: QuickView | null

  constructor() {
    super()
    this.variant = JSON.parse(
      $el('[data-selected-variant-json]', this).textContent,
    )
    this.quickView = this.closest('quick-view')
  }

  mountComponent() {
    this.setFirstMediaByVariant()
    this.setListeners()
  }

  handleVariantChange = event => {
    this.swapProduct(this.getFetchUrl(event.target))

    this.emit('change', {
      variant: this.variant,
      option: {
        name: event.target.name,
        value: event.target.value,
      },
    })
  }

  toggleHeaderDynamicShow = bool => {
    const header: HeaderComponent = $el(HEADER_SELECTOR)
    header.toggleLockDynamicShow(bool)
  }

  getFetchUrl(clickedInputEl: HTMLElement): string {
    const options = $list(OPTION_INPUTS_SELECTOR, this)
    const selectedOptionsIds = options.map(
      option => this.getCheckedInput($list('input', option)).dataset.valueId,
    )

    const params =
      selectedOptionsIds.length > 0
        ? `option_values=${selectedOptionsIds.join(',')}`
        : ''

    const baseUrl = clickedInputEl?.dataset.productUrl || this.dataset.url

    const url = `${baseUrl}${
      this.quickView ? '/gsc-quick-view' : ''
    }?section_id=${this.dataset.sectionIdWithoutPrefix}&${params}`

    return url
  }

  swapProduct(url: string): void {
    this.getForm().setDisable(true)
    this.getForm().setLoading(true)

    fetch(url)
      .then(response => response.text())
      .then(this.updateDom)
  }

  updateDom = (responseText: string): void => {
    const html = new DOMParser().parseFromString(responseText, 'text/html')

    if (this.quickView) {
      this.quickView.setProductFromHTML(html)
    } else {
      const { newProduct, newVariantPicker } = this.getNewProductData(html)

      this.removeAnimationClass(newProduct)

      this.updateURL(newVariantPicker)

      const shouldSwapProduct =
        this.dataset.url !== newVariantPicker.dataset.url ||
        this.hasAttribute('data-product-media-filtering-option')

      this.updateElements(newProduct, shouldSwapProduct)
    }
  }

  updateElements(html: HTMLElement, shouldSwapProduct: boolean): void {
    const product = this.closest('.shopify-section')

    if (shouldSwapProduct) {
      $el(`[${PRODUCT_MEDIA_SELECTOR}]`, product).replaceWith(
        $el(`[${PRODUCT_MEDIA_SELECTOR}]`, html),
      )
    }

    $list(`[${PRODUCT_BLOCK_SELECTOR}]`, product).forEach(block => {
      block.replaceWith(
        $el(
          `[${PRODUCT_BLOCK_SELECTOR}='${block.getAttribute(
            PRODUCT_BLOCK_SELECTOR,
          )}']`,
          html,
        ),
      )
    })
  }

  updateURL(html: HTMLElement): void {
    if (this.dataset.updateUrl === 'false') return

    const newUrl = html.dataset.url
    const newVariant = html.dataset.selectedVariant

    window.history.replaceState(
      {},
      '',
      `${newUrl}${newVariant ? `?variant=${newVariant}` : ''}`,
    )
  }

  getNewProductData = (html: Document) => {
    const newProduct = html.getElementById(
      `shopify-section-${this.dataset.sectionIdWithoutPrefix}`,
    )

    const newVariantPicker = $el('variant-picker', newProduct)

    return { newProduct, newVariantPicker }
  }

  removeAnimationClass(el): void {
    $list('.scroll-trigger', el).forEach(el => {
      el.classList.remove('scroll-trigger')
    })
  }

  setListeners(): void {
    const dropdowns: FloatElement[] = $list('float-element', this)
    const dropdownLabels = dropdowns
      .map(dropdown => $list('label', dropdown.element))
      .flat(1)
    const simpleLabels = $list('label', this)
    const labels = [...dropdownLabels, ...simpleLabels]

    this.addListener(this, 'keydown', this.handleKeydown)

    labels.forEach(label => {
      this.addListener(label, 'keydown', this.handleLabelKeydown)
    })

    this.addListener(this, 'change', this.handleVariantChange)
  }

  handleLabelKeydown = (event: KeyboardEvent): void => {
    if (isEnterKey(event)) {
      event.preventDefault()
      const target = event.target as HTMLElement
      const input = $el(`input[id="${target.getAttribute('for')}"]`, this)

      if (input) {
        input.dispatchEvent(new MouseEvent('click', { button: 0, which: 1 }))
      }
    }
  }

  handleKeydown = (event: KeyboardEvent): void => {
    const isArrowKey = ARROW_KEYS.includes(event.key)

    if (!isArrowKey && !isTabKey(event)) {
      return
    }

    const target = event.target as HTMLElement
    const labelContainer = $elParent(OPTION_LABELS_SELECTOR, target)

    if (!labelContainer) {
      this.removeListener(document, 'keydown', this.handleLabelKeydown)
      return
    }

    const labels = $list('label', labelContainer)
    const index = labels.findIndex(label => label === event.target)

    const currentLabel = labels[index]
    const firstLabel = labels[0]
    const lastLabel = labels[labels.length - 1]
    const nextLabel = labels[index + 1]
    const prevLabel = labels[index - 1]

    const setActiveLabel = newLabel => {
      currentLabel?.removeAttribute('tabindex')
      newLabel.setAttribute('tabindex', 0)
      newLabel.focus()
      event.preventDefault()
    }

    const changeActiveLabelOnNext = () => {
      setActiveLabel(nextLabel || firstLabel)
    }

    const changeActiveLabelOnPrev = () => {
      setActiveLabel(prevLabel || lastLabel)
    }

    const callback = {
      ArrowLeft: changeActiveLabelOnPrev,
      ArrowRight: changeActiveLabelOnNext,
      ArrowUp: changeActiveLabelOnPrev,
      ArrowDown: changeActiveLabelOnNext,
    }[event.key]

    if (isTabKey(event)) {
      if (prevLabel && event.shiftKey) {
        changeActiveLabelOnPrev()
      } else if (nextLabel && !event.shiftKey) {
        changeActiveLabelOnNext()
      }
    } else {
      callback()
    }
  }

  async getDropdownElementBySelector(selector) {
    const dropdowns: FloatElement[] = $list('float-element', this)
    let elementBySelector

    await whenDefined('float-element').then(() => {
      elementBySelector = $el(
        selector,
        dropdowns.find(dropdown => $el(selector, dropdown.element)).element,
      )
    })

    return elementBySelector
  }

  setFirstMediaByVariant() {
    if (this.hasAttribute(SELECTED_VARIANT_IMAGE_SELECTOR)) {
      const mediaList: (ProductMediaCarousel | ProductMedia)[] = $list(
        `#product-media-${this.dataset.productId}-${this.dataset.section}`,
      )

      mediaList.forEach(media =>
        media.updateFirstMediaByVariant(
          this.getAttribute(SELECTED_VARIANT_IMAGE_SELECTOR),
        ),
      )
    }
  }

  getCheckedInput(inputs): HTMLInputElement {
    return inputs.find(({ checked }) => checked)
  }

  getForm(): ProductForm {
    return $el(
      `#product-form-component-${this.dataset.productId}-${this.dataset.section}`,
    )
  }
}
